import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { ToastContent } from "../../components/ToastContent/ToastContent";
import { translate } from "../../intl";
import { Response } from "../../types/serviceTypes";
import {
  workflowInstance_V2,
  workflowInstance_V3,
  workflowInstance_V4,
} from "../api";

const { get } = axios;

/////////////////
// CALL API V3 //
/////////////////

export const callWorkflowGET_V3 = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => callApiTyped<T>(() => workflowInstance_V3.get<T>(url, config));

export const callWorkflowPOST_V3 = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V3.post<T>(url, body, config));

export const callWorkflowPUT_V3 = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V3.put<T>(url, body, config));

export const callWorkflowDelete_V3 = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> =>
  withErrorHandler(() => workflowInstance_V3.delete<T>(url, config));

/////////////////
// CALL API V4 //
/////////////////

export const callWorkflowGET_V4 = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => callApiTyped<T>(() => workflowInstance_V4.get<T>(url, config));

export const callWorkflowPOST_V4 = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V4.post<T>(url, body, config));

export const callWorkflowPUT_V4 = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V4.put<T>(url, body, config));

export const callWorkflowDelete_V4 = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> =>
  withErrorHandler(() => workflowInstance_V4.delete<T>(url, config));

/////////////////
// CALL API V2 //
/////////////////

export const callWorkflowGET = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => callApiTyped<T>(() => workflowInstance_V2.get<T>(url, config));

export const callWorkflowPOST = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V2.post<T>(url, body, config));

export const callWorkflowPUT = <T, B>(
  url: string,
  body: B,
  config?: AxiosRequestConfig,
): Promise<T> =>
  callApiTyped<T>(() => workflowInstance_V2.put<T>(url, body, config));

export const callWorkflowDelete = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> =>
  withErrorHandler(() => workflowInstance_V2.delete<T>(url, config));

/////////////////
// CALL API V1 //
/////////////////

export const callGET = <T>(url: string) => callApiTyped<T>(() => get<T>(url));

export const callApi = <T>(callFn: () => Promise<T | any>): Promise<T | any> =>
  extractResponseData(withErrorHandler(callFn));

///////////////
// UTILITIES //
///////////////

export const callApiTyped = <T>(
  callFn: () => Promise<AxiosResponse<T>>,
): Promise<T> => extractResponseData(withErrorHandler(callFn));

export const withErrorHandler = async (
  asyncFn: () => Promise<AxiosResponse<any>>,
): Promise<AxiosResponse<any>> => {
  try {
    return await asyncFn();
  } catch (error) {
    await Promise.reject(error);
    throw error;
  }
};

export const extractResponseData = async (
  response: Promise<{ data: any }>,
): Promise<any> => {
  const { data } = await response;
  return data;
};

/////////////////////
// BUILDS RESPONSE //
/////////////////////

export const buildResponse = <T>(
  response: Promise<T>,
  setResponse: (obj: Response<T>) => void,
  action?: () => void,
): void => {
  setResponse({ data: undefined, loading: true, error: false });
  response
    .then((res) => {
      setResponse({ data: res, loading: false, error: false });
      if (action) action();
    })
    .catch(() => setResponse({ data: undefined, loading: false, error: true }));
};

export const buildResponseWithMapper = <T, R>(
  response: Promise<T>,
  setResponse: (obj: Response<R>) => void,
  responseTo: (obj: T) => R,
): void => {
  setResponse({ data: undefined, loading: true, error: false });
  response
    .then((res) =>
      setResponse({ data: responseTo(res), loading: false, error: false }),
    )
    .catch(() => setResponse({ data: undefined, loading: false, error: true }));
};

export const buildMAHResponsesWithMapper = <T, R>(
  responses: Promise<any>[],
  setResponse: (obj: Response<R>) => void,
  responseTo: (obj: T) => R,
  mergeMAHResponsesToMapper?: (res: T[]) => T,
): void => {
  setResponse({ data: undefined, loading: true, error: false });
  Promise.all(responses)
    .then((res) => {
      const mergedPropsToBeMapped = mergeMAHResponsesToMapper
        ? mergeMAHResponsesToMapper(res)
        : { ...res[0], ...res[1] };

      return setResponse({
        data: responseTo(mergedPropsToBeMapped),
        loading: false,
        error: false,
      });
    })
    .catch(() => setResponse({ data: undefined, loading: false, error: true }));
};

export const handleFeedback = <T>(
  response: Promise<T>,
  action?: (res: any) => void,
  message?: (res: T) => string,
): void => {
  response
    .then((res) => {
      toast.success(message?.(res) ?? translate("feedback.success"), {
        closeButton: true,
        draggable: false,
      });
      if (action) action(res);
    })
    .catch(() => {
      toast.error(ToastContent(translate("feedback.errorReloadPage"), "id"), {
        onClick: () => window.location.reload(),
        autoClose: false,
        closeButton: true,
        draggable: false,
      });
    });
};
